import PropTypes from "prop-types";
import classNames from "classnames";
import DOMPurify from "dompurify";

import "./HtmlText.css";

// Keep `target` on links: DOMPurify's default profile strips it, but a
// designer-authored target="_blank" must survive so the link opens in a new
// tab instead of navigating the patient away from a half-filled form (answers
// live only in the reducer and would be lost). Whenever a target is present,
// force rel="noopener noreferrer" as a reverse-tabnabbing guard. HtmlText is
// the only DOMPurify consumer in the app, so registering this hook once at
// module load is safe.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.hasAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const SANITIZE_CONFIG = { ADD_ATTR: ["target"] };

/**
 * Render an HTML string as HTML, sanitised with DOMPurify's default profile
 * (plus `target` on links — see the hook above).
 *
 * The producer (Designer server) also sanitises before persisting, but we
 * sanitise here too as defense in depth on every render path — most
 * importantly the /preview and print paths, where content arrives via
 * postMessage and never passes the server sanitiser.
 *
 * The default profile is deliberate (rather than a narrow allow-list): it
 * strips the actual XSS vectors — event-handler attributes, javascript: URLs,
 * <script>/<iframe> and friends — while preserving legitimately authored
 * formatting, images, tables and headings. So live rendering is unchanged for
 * real content, and preview stays a faithful representation of what the form
 * will look like (a narrow allow-list would strip a designer's own images and
 * tables from their preview). See docs/features/2-preview-route.md.
 */
export function HtmlText({
  className,
  element: HtmlTag,
  htmlString = "",
  htmlFor,
  id,
}) {
  const sanitized = DOMPurify.sanitize(htmlString, SANITIZE_CONFIG);

  return (
    <HtmlTag
      className={classNames({
        "html-text": true,
        [className]: !!className,
      })}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify above
      dangerouslySetInnerHTML={{ __html: sanitized }}
      htmlFor={htmlFor}
      id={id}
    />
  );
}

HtmlText.propTypes = {
  className: PropTypes.string,
  element: PropTypes.string.isRequired,
  htmlString: PropTypes.string,
  htmlFor: PropTypes.string,
  id: PropTypes.string,
};
